// Step 2 of the e-sign package wizard: "Who signs, and in what order"
// (Documents tab PR 8d; mockup v3 screen 7). Styles are ported from that
// file class by class (styles.css, `.sostep-*`).
//
// Shown by Prepare & Send before its field screen when a package comes
// from the wizard or a draft. It holds no state of its own: who is on the
// send, the casting, the order numbers and the copies all live on Prepare
// & Send, so going on to the fields and coming back loses nothing.
//
// Signing order is OFF by default ("everyone gets it at once"). Ticking
// "Set a signing order" gives every signer a 1; typing a number or
// dragging a row onto another changes it. The same number signs together,
// and number 2 is asked once everyone at number 1 has signed (the server
// rule is shared/esign/signing-order.ts).

import { useState, type ReactNode } from "react";
import { Stamp } from "lucide-react";

import { Button } from "../../ui";
import { WizardSteps } from "./PackageWizard";

export interface SignerRow {
  key: string;
  name: string;
  email: string;
  /** The seat ("Buyer 1"), when the send is cast. */
  roleLabel: string | null;
  /** That seat's catalog key, for the Role dropdown. */
  roleKey?: string | null;
  color: string;
}

export interface SignersAndOrderStepProps {
  eyebrow: string;
  signers: readonly SignerRow[];
  /** The sender's own stamped row, when the forms have a seat for them. */
  stamped: { name: string; email: string | null; roleLabel: string; color: string } | null;
  /** People on the send who only get a copy. */
  copyOnly: readonly SignerRow[];
  /** People on the record who could be added. */
  addable: readonly { key: string; name: string; email: string }[];
  enforceOrder: boolean;
  onEnforceOrder: (on: boolean) => void;
  orderByKey: Readonly<Record<string, number>>;
  onOrder: (key: string, order: number) => void;
  onRemove: (key: string) => void;
  /** Signing seats nobody holds yet, offered by the Role dropdown. Empty
   *  when the forms name no seats (nothing to choose, so Role stays text). */
  seatOptions?: readonly { key: string; label: string }[];
  /** The firm's standing seat holders (the managing broker) not yet on
   *  this package. One click adds them in their seat. */
  firmSuggestions?: readonly { roleKey: string; label: string; name: string }[];
  onAddFirm?: (roleKey: string) => void;
  /** Put a signer in a seat, or (null) in none. */
  onSeat?: (key: string, roleKey: string | null) => void;
  onAdd: (key: string, roleKey: string | null) => void;
  onMakeSigner: (key: string) => void;
  onAddSomeoneElse: (name: string, email: string, roleKey: string | null) => boolean;
  someoneElseError: string | null;
  /** Seats nobody is in, said out loud ("Seller 1 — nobody is in it…"). */
  emptySeats: readonly string[];
  /** The "Send a copy to…" picker (People & notes on this record). */
  copyPicker: ReactNode;
  onNext: () => void;
  onSaveDraft: (() => void) | null;
  savingDraft: boolean;
  onCancel: () => void;
}

export function SignersAndOrderStep(props: SignersAndOrderStepProps) {
  const {
    eyebrow,
    signers,
    stamped,
    copyOnly,
    addable,
    enforceOrder,
    onEnforceOrder,
    orderByKey,
    onOrder,
    onRemove,
    seatOptions = [],
    firmSuggestions = [],
    onAddFirm,
    onSeat,
    onAdd,
    onMakeSigner,
    onAddSomeoneElse,
    someoneElseError,
    emptySeats,
    copyPicker,
    onNext,
    onSaveDraft,
    savingDraft,
    onCancel,
  } = props;
  const [adding, setAdding] = useState<"record" | "else" | null>(null);
  const [showCopies, setShowCopies] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [dragKey, setDragKey] = useState<string | null>(null);
  /** The seat a person being added will take ("" = no seat). */
  const [addSeat, setAddSeat] = useState("");
  // A seat filled since it was picked is no longer offered; fall back.
  const addSeatKey = seatOptions.some((o) => o.key === addSeat) ? addSeat : "";
  const canSeat = Boolean(onSeat) && seatOptions.length > 0;

  /** The "As:" picker shown while adding someone. */
  const addSeatPicker = canSeat ? (
    <select
      className="req-inp sostep-as"
      aria-label="Their role on these forms"
      value={addSeatKey}
      onChange={(e) => setAddSeat(e.target.value)}
    >
      <option value="">As: Signer (no seat)</option>
      {seatOptions.map((o) => (
        <option key={o.key} value={o.key}>
          As: {o.label}
        </option>
      ))}
    </select>
  ) : null;

  // With an order, rows read in signing order; ties keep their list order.
  const rows = enforceOrder
    ? [...signers].sort((a, b) => (orderByKey[a.key] ?? 99) - (orderByKey[b.key] ?? 99))
    : signers;

  return (
    <div className="sostep" data-testid="signers-and-order">
      <div className="sostep-card">
        <div className="sostep-head">
          <div className="sostep-eyebrow">{eyebrow}</div>
          <h2 className="sostep-title">Who signs, and in what order</h2>
        </div>
        <WizardSteps current={1} />
        <div className="sostep-body">
          <label className="sostep-noorder">
            <input
              type="checkbox"
              checked={enforceOrder}
              onChange={(e) => onEnforceOrder(e.target.checked)}
            />
            Set a signing order
            <span className="sostep-hint">
              {enforceOrder ? "Same number signs together" : "Off: everyone gets it at once"}
            </span>
          </label>

          <div className="sostep-shead" aria-hidden="true">
            <span />
            <span>Order</span>
            <span />
            <span>Person</span>
            <span>Email</span>
            <span>Role</span>
            <span />
          </div>
          {rows.map((r) => (
            <div
              key={r.key}
              className="sostep-row"
              data-dragover={dragKey && dragKey !== r.key ? "true" : undefined}
              onDragOver={(e) => {
                if (!enforceOrder || !dragKey) return;
                e.preventDefault();
              }}
              onDrop={(e) => {
                e.preventDefault();
                // Dropped on a row: it takes that row's number, so it signs
                // with them (mockup note 2).
                if (enforceOrder && dragKey && dragKey !== r.key) onOrder(dragKey, orderByKey[r.key] ?? 1);
                setDragKey(null);
              }}
            >
              <span
                className="sostep-grab"
                data-off={enforceOrder ? undefined : "true"}
                draggable={enforceOrder}
                onDragStart={(e) => {
                  setDragKey(r.key);
                  e.dataTransfer.effectAllowed = "move";
                  e.dataTransfer.setData("text/plain", r.key);
                }}
                onDragEnd={() => setDragKey(null)}
                aria-hidden="true"
              >
                ⋮⋮
              </span>
              {enforceOrder ? (
                <input
                  className="sostep-ord"
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={50}
                  aria-label={`Signing order for ${r.name}`}
                  value={orderByKey[r.key] ?? 1}
                  onChange={(e) => {
                    const n = Math.floor(Number(e.target.value));
                    if (Number.isFinite(n) && n >= 1 && n <= 50) onOrder(r.key, n);
                  }}
                />
              ) : (
                <span className="sostep-ord" data-off="true" aria-label="No signing order">
                  —
                </span>
              )}
              <span className="sostep-bar" style={{ background: r.color }} />
              <span className="sostep-cell sostep-name">{r.name}</span>
              <span className="sostep-cell sostep-email">{r.email}</span>
              {onSeat && (canSeat || r.roleKey) ? (
                // The Role is a choice (Nate, 2026-10-15): someone added by
                // hand read "Signer" and filled no seat, so Buyer 2's lines
                // printed empty with no way to say "he is Buyer 2".
                <select
                  className="sostep-role sostep-role--pick"
                  aria-label={`Role for ${r.name}`}
                  value={r.roleKey ?? ""}
                  onChange={(e) => onSeat(r.key, e.target.value || null)}
                >
                  {r.roleKey ? <option value={r.roleKey}>{r.roleLabel ?? "Signer"}</option> : null}
                  {seatOptions.map((o) => (
                    <option key={o.key} value={o.key}>
                      {o.label}
                    </option>
                  ))}
                  <option value="">Signer (no seat)</option>
                </select>
              ) : (
                <span className="sostep-role">{r.roleLabel ?? "Signer"}</span>
              )}
              <button
                type="button"
                className="sostep-rm"
                aria-label={`Take ${r.name} off this package`}
                onClick={() => onRemove(r.key)}
              >
                ✕
              </button>
            </div>
          ))}
          {stamped ? (
            <div className="sostep-row sostep-row--stamped">
              <span className="sostep-grab" data-off="true" aria-hidden="true">
                ⋮⋮
              </span>
              {/* DECISION: an icon, not the word "Stamped". The word filled the
                  56px order box edge to edge (Grant, 2026-10-27: "bad spacing");
                  the plain-English meaning moves beside the name instead. */}
              <span
                className="sostep-ord sostep-ord--stamped"
                role="img"
                aria-label="Signed with your saved signature"
                title="Signed with your saved signature"
              >
                <Stamp size={18} strokeWidth={1.5} aria-hidden="true" />
              </span>
              <span className="sostep-bar" style={{ background: stamped.color }} />
              <span className="sostep-cell sostep-name">
                {stamped.name} <span className="sostep-hint">you · your saved signature</span>
              </span>
              <span className="sostep-cell sostep-email">{stamped.email ?? ""}</span>
              <span className="sostep-role">{stamped.roleLabel}</span>
              <span />
            </div>
          ) : null}
          {signers.length === 0 ? <p className="sostep-hint sostep-empty">Nobody is signing yet.</p> : null}

          {copyOnly.length > 0 ? (
            <div className="sostep-copies">
              {copyOnly.map((c) => (
                <div key={c.key} className="sostep-copyrow">
                  <span>
                    <b>{c.name}</b> <small>· gets a copy, signs nothing</small>
                  </span>
                  <button type="button" className="doc-link-btn" onClick={() => onMakeSigner(c.key)}>
                    Make a signer
                  </button>
                </div>
              ))}
            </div>
          ) : null}

          <div className="sostep-links">
            <button
              type="button"
              className="doc-link-btn"
              disabled={addable.length === 0}
              title={addable.length === 0 ? "Everyone on this record is already on the package." : undefined}
              onClick={() => setAdding(adding === "record" ? null : "record")}
            >
              + Add a signer from this record
            </button>
            <button type="button" className="doc-link-btn" onClick={() => setAdding(adding === "else" ? null : "else")}>
              + Someone else (name and email)
            </button>
            <button type="button" className="doc-link-btn" onClick={() => setShowCopies((v) => !v)}>
              + Send a copy to…
            </button>
            {onAddFirm
              ? firmSuggestions.map((f) => (
                  <button key={f.roleKey} type="button" className="doc-link-btn" onClick={() => onAddFirm(f.roleKey)}>
                    + {f.name} as {f.label}
                  </button>
                ))
              : null}
          </div>
          {adding === "record" ? (
            <div className="sostep-else">
              {addSeatPicker}
              <select
              className="req-inp sostep-pick"
              aria-label="Add a signer from this record"
              defaultValue=""
              onChange={(e) => {
                if (e.target.value) onAdd(e.target.value, addSeatKey || null);
                setAddSeat("");
                setAdding(null);
              }}
            >
              <option value="" disabled>
                Pick someone on this record…
              </option>
              {addable.map((p) => (
                <option key={p.key} value={p.key}>
                  {p.name} · {p.email}
                </option>
              ))}
            </select>
            </div>
          ) : null}
          {adding === "else" ? (
            <div className="sostep-else">
              <input
                className="req-inp"
                aria-label="Their name"
                placeholder="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <input
                className="req-inp"
                aria-label="Their email"
                placeholder="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              {addSeatPicker}
              <Button
                variant="sw-navy"
                size="sm"
                onClick={() => {
                  if (onAddSomeoneElse(name, email, addSeatKey || null)) {
                    setName("");
                    setEmail("");
                    setAddSeat("");
                    setAdding(null);
                  }
                }}
              >
                Add
              </Button>
              {someoneElseError ? <p className="req-error sostep-else-err">{someoneElseError}</p> : null}
            </div>
          ) : null}
          {showCopies ? (
            <div className="sostep-copyto">
              <div className="sostep-copyto-t">Send a copy to · from People &amp; notes on this record</div>
              {copyPicker}
            </div>
          ) : null}

          <div className="sostep-callout">
            {enforceOrder ? (
              <>
                <b>Same number signs together.</b> Number 2 gets theirs when everyone at number 1 has
                signed. Your own lines are signed before it goes out, so you never receive an envelope.
              </>
            ) : (
              <>
                <b>No signing order: everyone gets the email at once.</b> Tick "Set a signing order" to
                number the rows; same number signs at the same time, and order 2 gets theirs when order 1
                is done. Your own lines are signed before it goes out, so you never receive an envelope.
              </>
            )}
          </div>
          {emptySeats.map((msg) => (
            <div key={msg} className="sostep-callout sostep-callout--warn">
              {msg}
            </div>
          ))}
        </div>
        <div className="sostep-foot">
          {onSaveDraft ? (
            <button type="button" className="doc-link-btn" disabled={savingDraft} onClick={onSaveDraft}>
              {savingDraft ? "Saving…" : "Save as draft"}
            </button>
          ) : (
            <span />
          )}
          <span className="sostep-foot-r">
            <Button variant="sw-default" onClick={onCancel} data-no-autosave>
              Cancel
            </Button>
            <Button variant="primary" onClick={onNext} disabled={signers.length === 0}>
              Next: Prepare fields
            </Button>
          </span>
        </div>
      </div>
    </div>
  );
}
